import React from 'react';
import PageBreadcrumb from './PageBreadcrumb';

export { PageBreadcrumb };
export { pageTabTriggerClass } from './pageTabClass';

/**
 * Standard page wrapper (padding and max-width).
 * Use for top-level page content.
 */
export function PageContainer({ children, className = '' }: any) {
  return <div className={`page-enter px-4 py-6 sm:px-0 ${className}`.trim()}>{children}</div>;
}

/**
 * Page toolbar: optional breadcrumb, then title on the left, actions on the right.
 * @param {string|number|React.ReactNode} title - Plain string/number uses truncate; a React node (e.g. title + icon) uses a flex row layout.
 * @param {React.ReactNode} [children] - Action buttons (right)
 * @param {React.ReactNode} [description] - Optional subtitle under the title (left column)
 * @param {Array<{ label: string, to?: string }>} [breadcrumbItems] - Trail; last item is current page (omit `to`)
 */
export function PageToolbar({ title, children, breadcrumbItems, description, titleSize = 'lg' }: any) {
  const hasBreadcrumb = Array.isArray(breadcrumbItems) && breadcrumbItems.length > 0;
  const titleIsPlainText = typeof title === 'string' || typeof title === 'number';
  const titleClassName = titleIsPlainText
    ? titleSize === 'sm'
      ? 'min-w-0 truncate text-2xl font-bold text-slate-100'
      : 'min-w-0 truncate text-3xl font-bold text-slate-100'
    : 'flex min-w-0 flex-wrap items-center justify-start gap-2 text-2xl font-bold text-slate-100 sm:text-3xl';

  return (
    <div className="mb-6">
      {hasBreadcrumb && (
        <div className="mb-2 min-w-0">
          <PageBreadcrumb items={breadcrumbItems} />
        </div>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className={titleClassName}>{title}</h1>
          {description != null && description !== false ? (
            <div className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
              {description}
            </div>
          ) : null}
        </div>
        {children != null && children !== false && (
          <div className="page-toolbar-actions-mdl flex flex-shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Section title (e.g. "Organisation members", "Pending invites").
 */
export function SectionTitle({ children, className = '', ...rest }: any) {
  return (
    <h2 className={`section-title-mdl ${className}`.trim()} {...rest}>
      {children}
    </h2>
  );
}

/**
 * Dense subsection heading inside cards or panels (e.g. roster panels, cycle overview).
 */
export function SectionSubtitle({ children, className = '', ...rest }: any) {
  return (
    <h2 className={`section-subtitle-mdl ${className}`.trim()} {...rest}>
      {children}
    </h2>
  );
}

/**
 * Empty state: icon, message, optional secondary text, optional CTA.
 */
export function EmptyState({ icon, title, description, action }: any) {
  return (
    <div className="empty-state-mdl">
      {icon}
      <p className="mt-4 text-slate-400">{title}</p>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      {action}
    </div>
  );
}

/**
 * Horizontal divider between sections (Material-style).
 */
export function SectionDivider() {
  return <hr className="section-divider-mdl" aria-hidden="true" />;
}

/**
 * Divider below the page toolbar (separates header from content).
 */
export function ToolbarDivider() {
  return <hr className="toolbar-divider-mdl" aria-hidden="true" />;
}
